import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { Subject, interval } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

import { AppError } from 'src/app/core/models/api-error';
import { ErrorMessageService } from 'src/app/core/services/error-message.service';
import { secondsUntilOdooDateTime } from 'src/app/shared/utils/helpers';
import { AuthErrorBannerComponent } from 'src/app/ui/auth-layout/auth-error-banner.component';
import { UiModule } from 'src/app/ui/ui.module';

import { MfaEnrollResult, MfaMethod, MfaStatus } from '../../../models/mfa.models';
import { MfaService } from '../../../services/mfa.service';

type WizardStep = 'setup' | 'verify';

/**
 * One enrolment attempt: show the seed, take a code, hand back the status.
 *
 * Standalone and shared by `settings/security` and the prompt shown after
 * login, so the QR fallbacks and the resend throttle exist once. Hosts mount
 * it per attempt behind an `*ngIf` and pass the method they are enrolling:
 * the TOTP seed then dies with the component rather than living on in a host
 * that has moved on.
 */
@Component({
  selector: 'app-mfa-enrolment-wizard',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    UiModule,
    AuthErrorBannerComponent,
  ],
  templateUrl: './mfa-enrolment-wizard.component.html',
  styleUrls: ['./mfa-enrolment-wizard.component.scss'],
})
export class MfaEnrolmentWizardComponent implements OnInit, OnDestroy {
  static readonly CODE_PATTERN = /^\d{6}$/;

  /** Which factor to enrol. Read once, at mount. */
  @Input() method: MfaMethod = 'totp';

  /** The status the confirmation returned, so the host need not re-read it. */
  @Output() enrolled = new EventEmitter<MfaStatus | null>();
  @Output() cancelled = new EventEmitter<void>();

  step: WizardStep = 'setup';
  enrolment: MfaEnrollResult | null = null;
  error: AppError | null = null;
  loading = false;

  codeForm!: FormGroup;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly fb: FormBuilder,
    private readonly mfa: MfaService,
    private readonly errorMessages: ErrorMessageService
  ) {}

  ngOnInit(): void {
    this.codeForm = this.fb.group({
      code: [
        '',
        Validators.compose([
          Validators.required,
          Validators.pattern(MfaEnrolmentWizardComponent.CODE_PATTERN),
        ]),
      ],
    });

    // Only to tick change detection so the countdown renders. `resendWait` is
    // computed on read, or it would sit a second stale after every send.
    interval(1000).pipe(takeUntil(this.destroy$)).subscribe();

    this.start();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Seconds left on the send throttle, 0 when a resend is allowed. The server
   * refuses one inside 60s with a 429, which would otherwise surface as a bare
   * error with no hint of when to try again.
   */
  get resendWait(): number {
    return secondsUntilOdooDateTime(this.enrolment?.next_allowed_at);
  }

  private start(): void {
    this.error = null;
    this.loading = true;
    this.mfa
      .enroll(this.method)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.loading = false))
      )
      .subscribe({
        next: (response) => {
          this.enrolment = response.object ?? null;
          this.codeForm.reset({ code: '' });
          // An email code is already in the inbox; only TOTP has a seed to
          // show first.
          this.step = this.method === 'totp' ? 'setup' : 'verify';
        },
        error: (error) => this.fail(error),
      });
  }

  /** Ask again after a failed start. The host mounts this component once per
   *  attempt, so without it a failed `enroll` ends the attempt. */
  retry(): void {
    if (this.loading) {
      return;
    }
    this.start();
  }

  goToVerify(): void {
    this.error = null;
    this.step = 'verify';
  }

  confirm(): boolean {
    if (this.codeForm.controls['code'].invalid) {
      this.codeForm.markAllAsTouched();
      return false;
    }
    this.error = null;
    this.loading = true;
    this.mfa
      .confirm(this.method, this.codeForm.controls['code'].value)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.loading = false))
      )
      .subscribe({
        next: (response) => {
          // The seed must not outlive the enrolment.
          this.enrolment = null;
          this.codeForm.reset({ code: '' });
          this.enrolled.emit(response.object ?? null);
        },
        error: (error) => {
          this.fail(error);
          this.codeForm.reset({ code: '' });
        },
      });
    return true;
  }

  resendCode(): void {
    if (this.resendWait > 0) {
      return;
    }
    this.error = null;
    this.loading = true;
    this.mfa
      .resendEnrolmentCode()
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.loading = false))
      )
      .subscribe({
        next: (response) => {
          if (this.enrolment) {
            this.enrolment = {
              ...this.enrolment,
              masked_email: response.object?.masked_email ?? this.enrolment.masked_email,
              next_allowed_at: response.object?.next_allowed_at ?? null,
            };
          }
        },
        error: (error) => this.fail(error),
      });
  }

  cancel(): void {
    this.error = null;
    this.enrolment = null;
    this.codeForm.reset({ code: '' });
    this.cancelled.emit();
  }

  // ui-form-field preventDefaults Enter, killing the implicit submit, so the
  // form needs a bridge. Inputs only: a button already turns Enter into a
  // click.
  onEnterConfirm(event: Event): void {
    if ((event.target as HTMLElement | null)?.tagName === 'INPUT') {
      this.confirm();
    }
  }

  private fail(error: unknown): void {
    this.error = this.errorMessages.parse(error as never);
  }
}
